import React, { useMemo, useState } from 'react';
import { Box, IconButton, Typography, Collapse, Theme } from '@material-ui/core';
import { ExpandMore, ExpandLess, Email, Notifications, Schedule } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { StringParam, useQueryParam } from 'use-query-params';
import { Skeleton } from '@material-ui/lab';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { TemplateItem } from './TemplateItem';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import {
  GetCommunicationTemplate,
  GetCommunicationTemplate_vm_communicationTemplates as TemplateType,
} from './__generated__/GetCommunicationTemplate';
import { GET_COMMUNICATION_TEMPLATE } from './communication-template-model.gql';
import { TemplateItemContent } from './TemplateItemContent/TemplateItemContent';
import { useDeepEffect } from '../../../../../hooks/useDeepEffect';
import { GET_TENANT_CONFIG } from './tenant-config-model.gql';
import { GetTenantConfig } from './__generated__/GetTenantConfig';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    searchHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
      marginTop: '1rem',
    },
    searchLabel: {
      fontWeight: 600,
      fontSize: '14px',
      color: '#666',
    },
    section: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '25px',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
    },
    sectionHeaderExpanded: {
      paddingBottom: '1rem',
      borderBottom: `1px solid #efefef`,
      marginBottom: '1rem',
    },
    sectionInfo: {
      flex: 1,
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
    },
    sectionIcon: {
      fontSize: '14px',
      paddingRight: '5px',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      lineHeight: '1.4',
    },
    expandIcon: {
      padding: '4px',
      color: theme.color.grey.neutralBrand500,
    },
    sectionContent: {
      display: 'flex',
      alignItems: 'stretch',
      height: '400px',
      margin: '0 -1.875rem 0 -1.875rem', // Negative margin to extend to edges, keep bottom padding
      paddingBottom: '0.625rem',
    },
    list: {
      flexBasis: '330px',
      height: '100%',
      overflowY: 'auto',
      backgroundColor: 'white',
      paddingLeft: '1.875rem', // Match left padding with section
      paddingRight: '0', // No right padding - flush against content
    },
    content: {
      flex: 1,
      backgroundColor: '#54545440',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      padding: '0',
      marginRight: '1.875rem', // Right margin to match section padding
      fontSize: '13px', // Smaller font size for better visibility
    },
  })
);

export function TemplateContent() {
  const classes = useStyles();

  const { data, loading: communicationTemplateLoading } = useCampfireQuery<GetCommunicationTemplate, {}>(
    GET_COMMUNICATION_TEMPLATE,
    {}
  );
  const { data: tenantData, loading: tenantDataLoading } = useCampfireQuery<GetTenantConfig, {}>(GET_TENANT_CONFIG, {});

  const [q, setQ] = useQueryParam('q', StringParam);

  // Section expansion states
  const [generalExpanded, setGeneralExpanded] = useState(false);
  const [remindersExpanded, setRemindersExpanded] = useState(false);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);

  // Selected template IDs for each section
  const [generalTemplateId, setGeneralTemplateId] = useState<string>();
  const [remindersTemplateId, setRemindersTemplateId] = useState<string>();
  const [notificationsTemplateId, setNotificationsTemplateId] = useState<string>();

  const templates = data?.vm.communicationTemplates.concat(data?.im.communicationTemplates) || [];

  // Filter and sort all templates, then categorize by category field
  const categorizedTemplates = useMemo(() => {
    const filtered = templates
      .filter((temp) => !temp.isHidden)
      .filter((temp) => (q ? temp.name.toLowerCase().includes(q.toLowerCase()) : true))
      .sort((a, b) => a.name.localeCompare(b.name));

    const general: TemplateType[] = [];
    const reminders: TemplateType[] = [];
    const notifications: TemplateType[] = [];

    filtered.forEach((template) => {
      const category = template.category?.toLowerCase() || 'general';

      if (category === 'reminder') {
        reminders.push(template);
      } else if (category === 'notification') {
        notifications.push(template);
      } else {
        general.push(template);
      }
    });

    return { general, reminders, notifications };
  }, [templates, q]);

  // Set initial template selections
  useDeepEffect(() => {
    if (categorizedTemplates.general.length > 0 && !generalTemplateId) {
      setGeneralTemplateId(categorizedTemplates.general[0]?.communicationTypeId);
    }
    if (categorizedTemplates.reminders.length > 0 && !remindersTemplateId) {
      setRemindersTemplateId(categorizedTemplates.reminders[0]?.communicationTypeId);
    }
    if (categorizedTemplates.notifications.length > 0 && !notificationsTemplateId) {
      setNotificationsTemplateId(categorizedTemplates.notifications[0]?.communicationTypeId);
    }
  }, [categorizedTemplates]);

  if (communicationTemplateLoading) {
    return (
      <>
        <Skeleton variant='rect' width='100%' height={100} style={{ marginBottom: '20px' }} />
        <Skeleton variant='rect' width='100%' height={100} style={{ marginBottom: '20px' }} />
        <Skeleton variant='rect' width='100%' height={100} />
      </>
    );
  }

  return (
    <>
      {/* General Section */}
      <Box className={classes.section}>
        <Box
          className={`${classes.sectionHeader} ${generalExpanded ? classes.sectionHeaderExpanded : ''}`}
          onClick={() => setGeneralExpanded(!generalExpanded)}
        >
          <Box className={classes.sectionInfo}>
            <Typography className={classes.sectionTitle}>
              <Email className={classes.sectionIcon} /> General
            </Typography>
            <Typography className={classes.sectionDescription}>
              Core communication templates for standard volunteer interactions and system messages
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {generalExpanded && <SearchField value={q} onChange={(event) => setQ(event.target.value)} />}
            <IconButton size='small' className={classes.expandIcon}>
              {generalExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={generalExpanded}>
          <Box className={classes.sectionContent}>
            <Box className={classes.list}>
              {categorizedTemplates.general.map((template) => (
                <TemplateItem
                  title={template.name}
                  key={template.communicationTypeId}
                  type={template.type}
                  enabled={Boolean(template.status)}
                  lastUpdated=''
                  onSelect={() => setGeneralTemplateId(template.communicationTypeId)}
                  isActive={template.communicationTypeId === generalTemplateId}
                />
              ))}
            </Box>
            <Box className={classes.content}>
              <TemplateItemContent
                communicationTypeId={generalTemplateId}
                greeting={tenantData?.tm.tenant.config?.communication?.greeting}
                signoff={tenantData?.tm.tenant.config?.communication?.signoff}
                logo={tenantData?.tm.tenant.config?.logo}
                tenantName={tenantData?.tm.tenant.name}
                isLoading={tenantDataLoading}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Reminders & Automations Section */}
      <Box className={classes.section}>
        <Box
          className={`${classes.sectionHeader} ${remindersExpanded ? classes.sectionHeaderExpanded : ''}`}
          onClick={() => setRemindersExpanded(!remindersExpanded)}
        >
          <Box className={classes.sectionInfo}>
            <Typography className={classes.sectionTitle}>
              <Schedule className={classes.sectionIcon} /> Reminders & Automations
            </Typography>
            <Typography className={classes.sectionDescription}>
              Automated reminders and follow-ups sent to volunteers before and after activities
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {remindersExpanded && <SearchField value={q} onChange={(event) => setQ(event.target.value)} />}
            <IconButton size='small' className={classes.expandIcon}>
              {remindersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={remindersExpanded}>
          <Box className={classes.sectionContent}>
            <Box className={classes.list}>
              {categorizedTemplates.reminders.map((template) => (
                <TemplateItem
                  title={template.name}
                  key={template.communicationTypeId}
                  type={template.type}
                  enabled={Boolean(template.status)}
                  lastUpdated=''
                  onSelect={() => setRemindersTemplateId(template.communicationTypeId)}
                  isActive={template.communicationTypeId === remindersTemplateId}
                />
              ))}
            </Box>
            <Box className={classes.content}>
              <TemplateItemContent
                communicationTypeId={remindersTemplateId}
                greeting={tenantData?.tm.tenant.config?.communication?.greeting}
                signoff={tenantData?.tm.tenant.config?.communication?.signoff}
                logo={tenantData?.tm.tenant.config?.logo}
                tenantName={tenantData?.tm.tenant.name}
                isLoading={tenantDataLoading}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Notifications Section */}
      <Box className={classes.section}>
        <Box
          className={`${classes.sectionHeader} ${notificationsExpanded ? classes.sectionHeaderExpanded : ''}`}
          onClick={() => setNotificationsExpanded(!notificationsExpanded)}
        >
          <Box className={classes.sectionInfo}>
            <Typography className={classes.sectionTitle}>
              <Notifications className={classes.sectionIcon} /> Notifications
            </Typography>
            <Typography className={classes.sectionDescription}>
              System notifications triggered by specific events like applications, cancellations, and roster updates
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {notificationsExpanded && <SearchField value={q} onChange={(event) => setQ(event.target.value)} />}
            <IconButton size='small' className={classes.expandIcon}>
              {notificationsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={notificationsExpanded}>
          <Box className={classes.sectionContent}>
            <Box className={classes.list}>
              {categorizedTemplates.notifications.map((template) => (
                <TemplateItem
                  title={template.name}
                  key={template.communicationTypeId}
                  type={template.type}
                  enabled={Boolean(template.status)}
                  lastUpdated=''
                  onSelect={() => setNotificationsTemplateId(template.communicationTypeId)}
                  isActive={template.communicationTypeId === notificationsTemplateId}
                />
              ))}
            </Box>
            <Box className={classes.content}>
              <TemplateItemContent
                communicationTypeId={notificationsTemplateId}
                greeting={tenantData?.tm.tenant.config?.communication?.greeting}
                signoff={tenantData?.tm.tenant.config?.communication?.signoff}
                logo={tenantData?.tm.tenant.config?.logo}
                tenantName={tenantData?.tm.tenant.name}
                isLoading={tenantDataLoading}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>
    </>
  );
}
